import React from 'react';
import { Target, Heart, Eye, Award } from 'lucide-react';
import { Card } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: <Award className="w-10 h-10" />,
      title: 'Quality',
      description: 'We never compromise on the quality of products and services we deliver to our customers.'
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Customer Satisfaction',
      description: 'Creating memorable experiences and exceeding expectations at every touchpoint.'
    },
    {
      icon: <Target className="w-10 h-10" />,
      title: 'Design & Experience',
      description: 'We believe that presentation, detail, and storytelling create lasting impressions.'
    },
    {
      icon: <Eye className="w-10 h-10" />,
      title: 'Long-Term Brand Building',
      description: 'Building sustainable partnerships that deliver value for generations.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-black text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/34823910/pexels-photo-34823910.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="About Amar Stars"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              About Amar Stars
            </h1>
            <p className="text-2xl md:text-3xl text-gray-300 leading-relaxed">
              Building bridges between global brands and Mongolian consumers since 2010
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Who We Are
              </h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  Amar Stars LLC has been importing and representing international brands in Mongolia since 2010. The company has built a strong reputation through carefully selected partnerships and a commitment to quality, innovation, and customer satisfaction.
                </p>
                <p>
                  Our diverse portfolio includes renowned international brands across beauty, lifestyle, and wellness categories. We serve as the official distributor for premium brands, ensuring that Mongolian consumers have access to authentic, high-quality products.
                </p>
                <p>
                  With over a decade of experience in trading and market understanding, our team brings deep expertise in brand development, retail operations, and customer experience. We operate 6 retail locations across Ulaanbaatar, creating refined in-store experiences that go beyond simple distribution.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?w=800&q=80"
                alt="Amar Stars retail experience"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-black text-white p-8 rounded-xl shadow-xl">
                <p className="text-5xl font-bold mb-2">14+</p>
                <p className="text-lg text-gray-300">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Amar Stars
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card 
                key={index}
                className="p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-gray-200"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-black text-white rounded-full mb-6">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our Vision
            </h2>
            <p className="text-2xl md:text-3xl text-gray-300 leading-relaxed font-light">
              To bring globally trusted brands to Mongolia while creating meaningful, high-quality experiences for modern consumers.
            </p>
            <p className="text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto">
              We envision a future where Mongolian consumers have seamless access to the world's best brands, supported by exceptional retail experiences and customer service that sets new standards in our market.
            </p>
          </div>
        </div>
      </section>

      {/* Brand Positioning */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gray-50 rounded-3xl p-12 md:p-16 text-center border-2 border-gray-200">
              <p className="text-3xl md:text-4xl text-gray-800 leading-relaxed font-light mb-8">
                <span className="font-semibold text-black">Amar Stars LLC</span> is a curator of global beauty, lifestyle, and wellness brands in Mongolia, delivering <span className="font-semibold text-black">quality, design, and trusted brand experiences</span> since 2010.
              </p>
              <div className="flex justify-center items-center space-x-12 pt-8 border-t border-gray-300">
                <div className="text-center">
                  <p className="text-4xl font-bold text-black mb-2">3</p>
                  <p className="text-gray-600">Premium Brands</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-black mb-2">6</p>
                  <p className="text-gray-600">Retail Locations</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-black mb-2">2010</p>
                  <p className="text-gray-600">Established</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
